/* eslint-disable react-refresh/only-export-components -- Context files intentionally export both a context object and a hook from the same module. Splitting would break the encapsulation of this pattern. */
/**
 * ApiContext — React Context for the Api dependency container
 *
 * Provides the Api instance to the component tree via React context.
 * The ApiProvider creates an authenticated axios instance with auth
 * interceptors and constructs the Api container.
 *
 * Pattern adapted from influenster-v4-mobile/src/hooks/ApiProvider/useApi.tsx
 *
 * Usage:
 *   // In App.tsx
 *   <ApiProvider>
 *     <App />
 *   </ApiProvider>
 *
 *   // In hooks
 *   const api = useApi();
 *   api.campaigns.campaignsControllerGetUsersCampaignsList();
 */

import React, { useMemo } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Api } from '@/api/Api';
import { ENV } from '@/config/env';

const USER_AGENT = 'Influenster - Creator Portal';

/**
 * Create an authenticated axios instance with request interceptors
 * that automatically attach auth headers to every request.
 */
function createAuthenticatedAxios() {
  const instance = axios.create();

  instance.interceptors.request.use(async (config) => {
    // Always include the user-agent header
    config.headers.set('x-infl-user-agent', USER_AGENT);

    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      if (accessToken && !config.headers.get('Authorization')) {
        config.headers.set('Authorization', `Bearer ${accessToken}`);
      }
    } catch {
      // Not authenticated — proceed without Authorization header
    }

    return config;
  });

  return instance;
}

type ApiContextData = Api | null;

export const ApiContext = React.createContext<ApiContextData>(null);

export const ApiProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const api = useMemo(() => {
    const authenticatedAxios = createAuthenticatedAxios();
    return new Api(authenticatedAxios, ENV.memberGatewayBasePath);
  }, []);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
};

/**
 * Hook to access the Api dependency container from context.
 *
 * @throws Error if used outside of ApiProvider
 * @returns The Api instance
 */
export function useApi(): Api {
  const api = React.useContext(ApiContext);
  if (!api) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return api;
}
